import { z } from 'zod';

import { BaseFlowBlockSchema, BlockType, RunMode } from '../base/baseSchema.js';
import { BlockValueUsPhoneNumberSchema } from '../base/blockValueUsPhoneNumberSchema.js';
import { BlockValueTemplateHint, validateBlockValueTemplate } from '../base/template.js';

export function validateBlockValueTemplateInString(
  value: string
): { template: string; hint: BlockValueTemplateHint; message?: string }[] {
  const templateRegex = /{{[^{}]+}}/g;
  const matches = value.match(templateRegex);

  if (!matches) return [];

  const templates: {
    template: string;
    hint: BlockValueTemplateHint;
    message?: string;
  }[] = [];
  for (const match of matches) {
    const validation = validateBlockValueTemplate(match);
    templates.push({
      template: match,
      hint: validation.hint,
      message: validation.hint !== BlockValueTemplateHint.Invalid ? undefined : validation.message,
    });
  }
  return templates;
}

const createDocumoCoversheetFieldSchema = (maxLength: number) => {
  return z
    .string()
    .optional()
    .superRefine((value, context) => {
      if (!value) return;

      const templates = validateBlockValueTemplateInString(value);
      if (templates.length > 0) {
        templates.forEach((template) => {
          if (template.hint === BlockValueTemplateHint.Invalid) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Invalid template "${template.template}": ${template.message}`,
            });
          }
        });
        return;
      }

      if (value.length > maxLength) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Input is longer than ${maxLength} characters.`,
        });
      }
    });
};

export const FaxBlockConfigSchema = z.object({
  fileReference: z.string(),
  faxNumber: BlockValueUsPhoneNumberSchema({ isRequired: true }),
  attachDocumoCoversheet: z.boolean().default(false),
  documoCoversheet: z
    .object({
      id: z.string(),
      recipientName: createDocumoCoversheetFieldSchema(40),
      senderName: createDocumoCoversheetFieldSchema(40),
      subject: createDocumoCoversheetFieldSchema(55),
      notes: createDocumoCoversheetFieldSchema(400),
    })
    .optional(),
  runMode: z.nativeEnum(RunMode).default(RunMode.Live),
});

export type FaxBlockConfig = z.infer<typeof FaxBlockConfigSchema>;

export const FaxBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.Fax),
  config: FaxBlockConfigSchema,
});

export type FaxBlock = z.infer<typeof FaxBlockSchema>;
